<!--
    PlanupCalendar - the calendar everywhere in the app: the יומן modal of the header (App.vue), the
    /calendar page, the dashboard's two-week strip (period="week" :periodCount="2") and the event date
    picker (disableClickDatePopup + confirmDateOnClick; its several-options tab marks the chosen days
    with selectedDates and toggles a day on chooseDate). Three views under one toolbar
    (CalendarToolbar): month (CalendarMonthGrid - a grid of day cells with tinted pills), week and
    day (the hourly tables below). Every item gets a hover card (CalendarHoverCard); a click on a day
    opens the day popup (what to do on that date, the day's agenda, open the date for online booking).

    Items come from calendarHandler.getItemsInMonth (item-mapper.js shapes them, a month at a time,
    cached): events, meetings, tasks, google events, date holds and personal items (אישי - מילואים /
    רופא, a block of time with no customer: PersonalItemModal); $store.state.calendarCheckboxes (the
    toolbar chips) filter them; the colors are the CSS
    variables of calendar-colors.js, set below per item type and status and overridden by the
    account's chosen colors (הגדרות → צבעי לוח שנה).

    if you want to edit this component to show different items:
        add a prop that would be the function to pull items by date,
        add a prop to disable the default filters
-->
<template>
    <div class="calendarWrapperInner pcal"
         style="background:white;position:relative;"
         :class="[`planupCalendarDisplay_${displayType.value}`, { showCrewMembers: showCrewMembers, planupCalendar_disableItemClicks: disableItemClicks }]">

        <CalendarToolbar :title="periodTitle"
                         :titleSamples="periodTitleSamples"
                         :showNav="!hideHeaderButtons"
                         :anchorDate="anchorDate"
                         :chips="filterChips"
                         :showCrewMembers="showCrewMembers"
                         :hideDisplayTypeSelector="hideDisplayTypeSelector"
                         :displayTypes="planupCalendarDisplayTypes"
                         :displayType="displayType"
                         @prev="shiftPeriod(-1)"
                         @next="shiftPeriod(1)"
                         @today="goToDate(new Date())"
                         @pick="goToDate($event)"
                         @toggle-chip="toggleChip"
                         @toggle-crew="showCrewMembers = !showCrewMembers"
                         @change-display="displayType = $event" />



        <!-- ======================================================================= week -->
        <div class="planupWeekView"
             v-if="displayType.value == 'week'">

            <div class="pcal-timegrid">

                <div class="custom-scrollbar pcal-timegrid-scroll">

                    <!-- the day names + the all-day items, sticky above the hours -->
                    <div class="pcal-timegrid-sticky">
                        <table class="timeslotTable">
                            <tr>
                                <td class="pcal-tg-hour"></td>
                                <td v-for="t in [0, 1, 2, 3, 4, 5, 6]"
                                    :key="t"
                                    class="pcal-tg-dayhead"
                                    :class="{ today: isToday(weekViewDaysOfWeek[t]) }">

                                    <div class="pcal-tg-dayname">{{ dayNames[t] }}</div>
                                    <div class="pcal-tg-daynum"
                                         v-if="weekViewDaysOfWeek[t]">{{ weekViewDaysOfWeek[t].getDate() }}</div>

                                    <div :class="timeslotClasses(weekItem)"
                                         v-for="weekItem in weekViewItems[t]"
                                         :key="weekItem.id"
                                         v-if="!weekItem.time"
                                         :data-item-id="weekItem.id"
                                         @click.stop="clickItem(weekItem)"
                                         @mouseenter="hoverEnter(weekItem, $event.currentTarget)"
                                         @mouseleave="hoverLeave">
                                        <span class="pcal-item-icon"><i :class="iconOf(weekItem)"></i></span>
                                        <span class="pcal-item-title">{{ weekItem.originalTitle }}</span>
                                    </div>

                                </td>
                                <td style="width:16px;"></td>
                            </tr>
                        </table>
                    </div>

                    <table class="timeslotTable"
                           style="margin-bottom:16px;">
                        <tr v-for="timeslot in dayTimeslots"
                            :key="timeslot">

                            <td class="pcal-tg-hour">{{ timeslot }}:00</td>

                            <td class="clickableTimeSlot pcal-tg-cell"
                                v-for="dayOfWeek in [0, 1, 2, 3, 4, 5, 6]"
                                :key="dayOfWeek"
                                :class="{ today: isToday(weekViewDaysOfWeek[dayOfWeek]) }"
                                @click="clickOnTimeSlot(timeslot, weekViewDaysOfWeek[dayOfWeek])">

                                <div :class="timeslotClasses(weekItem)"
                                     v-for="weekItem in weekViewItems[dayOfWeek]"
                                     :key="weekItem.id"
                                     v-if="weekItem.time && hourOf(weekItem.time) == timeslot"
                                     :data-item-id="weekItem.id"
                                     @click.stop="clickItem(weekItem)"
                                     @mouseenter="hoverEnter(weekItem, $event.currentTarget)"
                                     @mouseleave="hoverLeave">
                                    <span class="pcal-item-icon"><i :class="iconOf(weekItem)"></i></span>
                                    <span class="pcal-item-time">{{ weekItem.time }}</span>
                                    <span class="pcal-item-title">{{ weekItem.originalTitle }}</span>
                                    <div :class="['timeSlotElementEndTime', weekItem.itemType].concat(weekItem.colorClasses || [])"
                                         :style="calculateTimeslotStyle(weekItem)"></div>
                                </div>

                            </td>

                            <td style="width:16px;"></td>
                        </tr>
                    </table>

                </div>
            </div>
        </div>



        <!-- ======================================================================== day -->
        <div class="planupDayView"
             v-show="displayType.value == 'day'">

            <div class="pcal-timegrid">
                <table class="timeslotTable">

                    <tr class="clickableTimeSlot pcal-tg-allday"
                        @click="clickOnTimeSlot()">
                        <td class="pcal-tg-hour">כל היום</td>
                        <td class="pcal-tg-cell">
                            <div :class="timeslotClasses(singleDayItem)"
                                 v-for="singleDayItem in singleDayViewItems"
                                 :key="singleDayItem.id"
                                 v-if="!singleDayItem.time"
                                 :data-item-id="singleDayItem.id"
                                 @click.stop="clickItem(singleDayItem)"
                                 @mouseenter="hoverEnter(singleDayItem, $event.currentTarget)"
                                 @mouseleave="hoverLeave">
                                <span class="pcal-item-icon"><i :class="iconOf(singleDayItem)"></i></span>
                                <span class="pcal-item-title">{{ singleDayItem.originalTitle }}</span>
                                <span class="pcal-item-sub"
                                      v-if="singleDayItem.subtitle">{{ singleDayItem.subtitle }}</span>
                            </div>
                        </td>
                    </tr>

                    <tr v-for="timeslot in dayTimeslots"
                        :key="timeslot"
                        class="clickableTimeSlot"
                        @click="clickOnTimeSlot(timeslot)">

                        <td class="pcal-tg-hour">{{ timeslot }}:00</td>

                        <td class="pcal-tg-cell">
                            <div :class="timeslotClasses(singleDayItem)"
                                 v-for="singleDayItem in singleDayViewItems"
                                 :key="singleDayItem.id"
                                 v-if="singleDayItem.time && hourOf(singleDayItem.time) == timeslot"
                                 :data-item-id="singleDayItem.id"
                                 @click.stop="clickItem(singleDayItem)"
                                 @mouseenter="hoverEnter(singleDayItem, $event.currentTarget)"
                                 @mouseleave="hoverLeave">
                                <span class="pcal-item-icon"><i :class="iconOf(singleDayItem)"></i></span>
                                <span class="pcal-item-time">{{ singleDayItem.time }}<template v-if="singleDayItem.endTime">–{{ singleDayItem.endTime }}</template></span>
                                <span class="pcal-item-title">{{ singleDayItem.originalTitle }}</span>
                                <span class="pcal-item-sub"
                                      v-if="singleDayItem.subtitle">{{ singleDayItem.subtitle }}</span>
                                <div :class="['timeSlotElementEndTime', singleDayItem.itemType].concat(singleDayItem.colorClasses || [])"
                                     :style="calculateTimeslotStyle(singleDayItem)"></div>
                            </div>
                        </td>

                    </tr>
                </table>
            </div>

        </div>



        <!-- ====================================================================== month -->
        <CalendarMonthGrid v-show="displayType.value == 'month'"
                           ref="monthGrid"
                           :date="dateToShow"
                           :period="period"
                           :periodCount="periodCount"
                           :items="filteredItems"
                           :maxItems="maxItemsInDay"
                           :showCrew="showCrewMembers"
                           :minHeight="height"
                           :disableItemClicks="disableItemClicks"
                           :selectedDates="selectedDates"
                           @click-date="clickDate"
                           @click-item="clickItem"
                           @show-more="clickDate"
                           @swipe="onSwipe"
                           @item-enter="hoverEnter"
                           @item-leave="hoverLeave" />

        <CalendarHoverCard ref="hoverCard"
                           :disableOpen="disableItemClicks" />



        <!-- ================================================================== day popup -->
        <Modal :hideFooter="true"
               ref="clickDateModal"
               :hideHeader="confirmDateOnClick"
               :title="clickDateModalTitle"
               :center="confirmDateOnClick">

            <template v-slot:body>

                <div v-if="confirmDateOnClick"
                     style="user-select:none;">

                    <div style="text-align: center;">

                        <h2 class="swal2-title">
                            {{ $globals.niceDate(clickedDate) }}
                        </h2>
                        <div class="swal2-content"
                             style="margin-bottom:30px;">
                            האם להמשיך עם התאריך הזה?
                        </div>

                        <button class="btn btn-primary"
                                @click="$emit('chooseDate', clickedDate)"
                                style="padding:10px 32px;font-size:17px"
                                data-bs-dismiss="modal">
                            המשך
                        </button>
                        &nbsp;
                        &nbsp;
                        <button class="btn btn-light"
                                style="padding:10px 32px;font-size:17px"
                                data-bs-dismiss="modal">
                            חזור
                        </button>

                    </div>

                </div>

                <!-- the cards wrap: with אישי and the booking card there are five of them -->
                <div v-if="!confirmDateOnClick"
                     style="display:flex;flex-wrap:wrap;justify-content: center;align-items:center;">

                    <div v-show="!chosenEntityToCreateInPopup && !showBookingAvailabilityEditor"
                         class="card popupOptionBlock meeting"
                         @click="chosenEntityToCreateInPopup = 'meeting'">
                        <div>
                            <i class="fa-regular fa-handshake"></i>
                            <br>
                            {{ $t('setMeeting') }}
                        </div>
                    </div>

                    <div v-show="!chosenEntityToCreateInPopup && !showBookingAvailabilityEditor"
                         class="card popupOptionBlock event"
                         @click="chosenEntityToCreateInPopup = 'event'">
                        <div>
                            <i class="fa-solid fa-champagne-glasses"
                               style="transform:scale(0.88)"></i>
                            <br>
                            {{ $t('createEvent') }}
                        </div>
                    </div>

                    <div v-show="!chosenEntityToCreateInPopup && !showBookingAvailabilityEditor"
                         class="card popupOptionBlock task"
                         @click="chosenEntityToCreateInPopup = 'task'">
                        <div>
                            <i class="fa-solid fa-bullseye"
                               style="transform:scale(0.88)"></i>
                            <br>
                            {{ $t('createTask') }}
                        </div>
                    </div>

                    <!-- אישי: מילואים / רופא / חופשה - a block of time with no customer behind it -->
                    <div v-show="!chosenEntityToCreateInPopup && !showBookingAvailabilityEditor"
                         class="card popupOptionBlock personal"
                         data-popup-personal
                         @click="chosenEntityToCreateInPopup = 'personal'">
                        <div>
                            <i class="fa-solid fa-user-clock"
                               style="transform:scale(0.85)"></i>
                            <br>
                            אישי
                        </div>
                    </div>

                    <!-- Opens this one date for online booking (on top of the weekly hours) -->
                    <div v-show="!chosenEntityToCreateInPopup && !showBookingAvailabilityEditor && bookingFeatureEnabled"
                         class="card popupOptionBlock bookingAvailability"
                         @click="openBookingAvailabilityEditor()">
                        <div>
                            <i class="fa-regular fa-calendar-check"></i>
                            <br>
                            {{ $t('openForBooking') }}
                        </div>
                    </div>

                    <div v-show="chosenEntityToCreateInPopup && chosenEntityToCreateInPopup != 'event'"
                         class="card popupOptionBlock"
                         :class="chosenEntityToCreateInPopup"
                         @click="emitNewEntityChoice('withoutCustomer')">
                        <div>
                            <i class="fa-solid fa-user-slash"
                               style="transform:scale(0.75)"></i>
                            <br>
                            {{ $t('withoutCustomer') }}
                        </div>
                    </div>

                    <div v-show="chosenEntityToCreateInPopup"
                         class="card popupOptionBlock"
                         :class="chosenEntityToCreateInPopup"
                         @click="emitNewEntityChoice('newCustomer')">
                        <div>
                            <i class="fa-solid fa-user-plus"
                               style="transform:scale(0.75)"></i>
                            <br>
                            {{ $t('newCustomer') }}
                        </div>
                    </div>

                    <div v-show="chosenEntityToCreateInPopup"
                         class="card popupOptionBlock"
                         :class="chosenEntityToCreateInPopup"
                         @click="emitNewEntityChoice('existingCustomer')">
                        <div>
                            <i class="fa-solid fa-user-group"
                               style="transform:scale(0.75)"></i>
                            <br>
                            {{ $t('existingCustomer') }}
                        </div>
                    </div>

                </div>

                <!-- Booking availability of this single date: its own hours, closed, or back to
                     the weekly hours. Saved on the spot (booking/day-availability). -->
                <div v-if="showBookingAvailabilityEditor"
                     style="text-align:right;max-width:430px;margin:0 auto 10px">

                    <div v-if="loadingBookingAvailability"
                         style="color:#999;padding:8px 0">
                        <i class="fa fa-spinner fa-spin"></i>
                        טוען זמינות...
                    </div>

                    <template v-else>

                        <div style="display:flex;align-items:center;cursor:pointer;user-select:none;padding:4px 0"
                             @click="bookingEditorOpen = !bookingEditorOpen">
                            <span class="planup-checkbox planup-checkbox-purple"
                                  :class="{ checked: bookingEditorOpen }"
                                  style="position:relative;top:1px;flex:0 0 auto">
                                <i class="fa fa-check"></i>
                            </span>
                            <span style="margin-right:12px">פנוי לקביעת פגישות בתאריך הזה</span>
                        </div>

                        <div v-if="bookingEditorOpen"
                             style="display:flex;align-items:center;gap:8px;color:#666;font-size:14px;margin:6px 0 10px">
                            <span>מ-</span>
                            <WheelTimePicker v-model="bookingEditorFrom"
                                             :minuteStep="30"
                                             :clearable="false"></WheelTimePicker>
                            <span>עד</span>
                            <WheelTimePicker v-model="bookingEditorTo"
                                             :minuteStep="30"
                                             :clearable="false"></WheelTimePicker>
                        </div>

                        <div v-if="bookingDayInfo && bookingDayInfo.takenSlots"
                             style="font-size:13px;color:#e67e22;margin-bottom:6px">
                            {{ bookingDayInfo.takenSlots }} מועדים בתאריך הזה תפוסים ביומן ולא יוצעו
                        </div>
                        <div v-if="bookingDayInfo && !bookingDayInfo.inHorizon"
                             style="font-size:13px;color:#999;margin-bottom:6px">
                            התאריך מחוץ לטווח שבו לקוחות יכולים לקבוע — יש להרחיב אותו בהגדרות תיאום הפגישות
                        </div>
                        <div style="font-size:13px;color:#999;margin-bottom:10px">
                            השעות כאן חלות על התאריך הזה בלבד, במקום שעות הפעילות השבועיות.
                        </div>

                        <button class="btn btn-primary btn-sm"
                                :disabled="savingBookingAvailability"
                                @click="saveBookingAvailability">
                            {{ savingBookingAvailability ? 'שומר...' : 'שמירה' }}
                        </button>
                        <button v-if="bookingDayInfo && bookingDayInfo.overridden"
                                class="btn btn-light btn-sm"
                                style="margin-right:6px"
                                :disabled="savingBookingAvailability"
                                @click="clearBookingAvailability">
                            חזרה לשעות השבועיות
                        </button>
                        <span class="btn btn-link btn-sm"
                              @click="showBookingAvailabilityEditor = false">
                            חזרה
                        </span>

                    </template>

                    <hr>

                </div>

                <div v-show="!showDailyAgenda && itemsToShowInPopup.length">
                    <span style="color:#ab8ce4;cursor:pointer;user-select:none;"
                          @click="showDailyAgenda = true">

                        הצג לו"ז ימי
                        &nbsp;
                        <i class="fa-solid fa-sort-down"
                           style="font-size:17px;position:relative;top:-2px;"></i>

                    </span>
                    <br>
                    <br>
                </div>

                <div v-if="showDailyAgenda && itemsToShowInPopup.length">

                    <span style="color:#ab8ce4;cursor:pointer;user-select:none;"
                          @click="showDailyAgenda = false">

                        הסתר לו"ז יומי
                        &nbsp;
                        <i class="fa-solid fa-sort-up"
                           style="font-size:17px;position:relative;top:6px;"></i>
                    </span>

                    <br>
                    <br>

                </div>

                <transition name="smooth">

                    <div v-show="showDailyAgenda">
                        <table style="width:100%;"
                               class="busyDayPopupTable">
                            <tr v-for="item in itemsToShowInPopup"
                                :key="item.id"
                                @click="clickItem(item)">

                                <td class="pcal-agenda-time">
                                    {{ item.time ? item.time : 'כל היום' }}
                                </td>
                                <td style="text-align:center;width:100px;padding:5px">
                                    <span :class="['pcal-agenda-chip', item.itemType, 'clickDateModalRow_' + item.itemType].concat(item.colorClasses || [])">
                                        <i :class="iconOf(item)"></i>
                                        {{ typeLabel(item) }}
                                    </span>
                                </td>
                                <td>
                                    <div v-if="item.itemType == 'event'">
                                        <div v-html="$handlers.eventHandler.eventStatusIndicatorHTML(item.planupSystemStatus)">
                                        </div>
                                    </div>
                                </td>
                                <td class="pcal-agenda-muted">
                                    {{ item.crewMembers }}
                                </td>
                                <td class="pcal-agenda-muted">
                                    {{ item.place }}
                                </td>
                                <td style="padding-right:5px;">
                                    <span class="pcal-agenda-title">{{ item.originalTitle ? item.originalTitle : 'ללא' }}</span>
                                    <span class="pcal-agenda-sub"
                                          v-if="item.subtitle">{{ item.subtitle }}</span>
                                </td>

                            </tr>
                        </table>
                    </div>
                </transition>

                <br>
                <br>

                <span class="pull-right btn btn-light"
                      @click="setDayView">
                    מעבר לתצוגת יום
                </span>

            </template>

        </Modal>

    </div>
</template>
<script>
import CalendarToolbar from './CalendarToolbar.vue';
import CalendarMonthGrid from './CalendarMonthGrid.vue';
import CalendarHoverCard from './CalendarHoverCard.vue';
import axiosV2, { apiPost } from "../../core/http/axios_api_v2";
import WheelTimePicker from "../Common/WheelTimePicker";
import { itemIcon, ITEM_TYPE_LABELS } from '../../services/calendar/calendar-colors';
import { itemsOfDay } from '../../services/calendar/calendar-days';

const DAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const MONTH_YEAR = new Intl.DateTimeFormat('he', { month: 'long', year: 'numeric' });
const DAY_MONTH = new Intl.DateTimeFormat('he', { day: 'numeric', month: 'long' });
const FULL_DAY = new Intl.DateTimeFormat('he', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

function validDate(value) {
    if (!value)
        return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
}

function sundayOf(date) {
    const sunday = new Date(date);
    sunday.setHours(0, 0, 0, 0);
    sunday.setDate(sunday.getDate() - sunday.getDay());
    return sunday;
}

// "12 באוקטובר – 18 באוקטובר 2026"
function rangeTitle(start, end) {
    return DAY_MONTH.format(start) + ' – ' + DAY_MONTH.format(end) + ' ' + end.getFullYear();
}

// the titles a title function gives over a year of start days, one per shape: the digits as 0s,
// and of the shapes with the same words the longest (its numbers have more digits)
function titleShapes(titleOf) {
    const longest = {};
    const day = new Date(2026, 0, 1);
    for (let i = 0; i < 366; i++) {
        const shape = titleOf(day).replace(/\d/g, '0');
        const words = shape.replace(/0/g, '');
        if (!longest[words] || shape.length > longest[words].length)
            longest[words] = shape;
        day.setDate(day.getDate() + 1);
    }
    return Object.keys(longest).map(words => longest[words]);
}

function timeOf(item) {
    const time = Date.parse(item.startDate);
    return isNaN(time) ? 0 : time;
}

// the item's day as a local date (its date holds the wall clock in the UTC fields, calendar-days.js)
function localDayOf(item) {
    const date = new Date(item.startDate);
    if (isNaN(date.getTime()))
        return new Date();
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export default {
    name: "PlanupCalendar",
    props: {

        disableClickDatePopup: {
            default: false
        },

        hideDisplayTypeSelector: {
            default: false
        },

        confirmDateOnClick: {
            default: false,
        },
        showTodayAgendaOnPopup: {
            default: true
        },
        maxItemsInDay: {
            default: 3
        },
        disableItemClicks: {
            default: false,
        },
        period: {
            default: 'month'
        },
        periodCount: {
            default: 1
        },
        hideHeaderButtons: {
            default: false
        },
        height: {
            default: '770px'
        },
        persistDateToShow: {
            default: false
        },
        // days the month grid marks as chosen (the date picker's several options)
        selectedDates: {
            default: () => []
        }
    },
    watch: {

        // new items, or the chips changed: the week / day views follow (the month grid is computed)
        filteredItems() {
            if (this.weekViewDate)
                this.updateWeekViewDate(this.weekViewDate);
            if (this.singleDayViewDate)
                this.singleDayViewItems = this.findItemsInThisDay(this.singleDayViewDate);
        },

        displayType: {

            handler() {

                if (!this.singleDayViewDate)
                    this.singleDayViewDate = new Date();

                if (!this.weekViewDate) {
                    const sunday = sundayOf(new Date());
                    this.weekViewDate = sunday;
                    // running the watcher of weekViewDate manually
                    this.updateWeekViewDate(sunday);
                }

                this.hoverLeave();
            },
            immediate: true
        },

        singleDayViewDate(newVal) {

            this.singleDayViewItems = this.findItemsInThisDay(newVal);

            // a day of a month that is not loaded yet
            if (this.$handlers.calendarHandler.buildCacheKey(newVal) != this.currentCacheKey)
                this.updateItems(newVal);
        },

        weekViewDate(newVal) {

            this.updateWeekViewDate(newVal);
            this.updateItems(newVal);
        },

        chosenEntityToCreateInPopup(newVal) {

            if (newVal == 'task') {
                this.$refs.clickDateModal.hide();
                let task = this.$handlers.tasksHandler.generateLocalEmptyTask();
                task.date = this.clickedDate;
                this.$root.$emit('hideQuickCalendar');
                this.$root.$emit('openTaskModal', task);
            }

            // אישי: its own form (PersonalItemModal, App.vue) on the clicked day / hour
            if (newVal == 'personal') {
                this.$refs.clickDateModal.hide();
                let time = null;
                if (this.chosenTime || this.chosenTime === 0)
                    time = (this.chosenTime < 10 ? '0' : '') + this.chosenTime + ':00';
                this.$root.$emit('hideQuickCalendar');
                this.$root.$emit('openPersonalItemModal', null, this.clickedDate, time);
            }
        },
        dateToShow(newVal) {
            if (this.persistDateToShow) {
                this.$store.state.calendarDateToShow = newVal;
                localStorage.setItem('calendarDateToShow', newVal);
            }
            this.updateItems(newVal);
        }
    },
    computed: {

        dayNames() {
            return DAY_NAMES;
        },

        // the date the open view is on
        anchorDate() {
            switch (this.displayType.value) {
                case 'week':
                    return this.weekViewDate || new Date();
                case 'day':
                    return this.singleDayViewDate || new Date();
                default:
                    return validDate(this.dateToShow) || new Date();
            }
        },

        // the open view's title of the period that starts on a date:
        // אוקטובר 2026 / 12 באוקטובר – 18 באוקטובר 2026 / יום שלישי, 13 באוקטובר 2026
        periodTitleOf() {
            const view = this.displayType.value;
            if (view == 'day')
                return date => FULL_DAY.format(date);
            if (view != 'week' && this.period != 'week')
                return date => MONTH_YEAR.format(date);
            // the week view, or the dashboard's strip of weeks
            const days = view == 'week' ? 7 : 7 * (parseInt(this.periodCount) || 1);
            return start => {
                const end = new Date(start);
                end.setDate(end.getDate() + days - 1);
                return rangeTitle(start, end);
            };
        },

        periodTitle() {
            const date = this.anchorDate;
            // the strip of weeks starts on the Sunday of its date
            const strip = this.displayType.value == 'month' && this.period == 'week';
            return this.periodTitleOf(strip ? sundayOf(date) : date);
        },

        // every title the open view can show, one per shape: the toolbar lays them under the title,
        // so its box keeps one width and the arrows stay put from one period to the next
        periodTitleSamples() {
            return this.hideHeaderButtons ? [] : titleShapes(this.periodTitleOf);
        },

        // the toolbar chips = $store.state.calendarCheckboxes; the classes carry the chip's color
        filterChips() {
            const checkboxes = this.$store.state.calendarCheckboxes;
            const chips = [
                { key: 'showEvents', label: 'אירועים', cls: 'event pcSys_hazmanaSigned', checked: !!checkboxes.showEvents },
                { key: 'showMaybeEvents', label: 'פניות חדשות', cls: 'pcSys_newLead', checked: !!checkboxes.showMaybeEvents },
                { key: 'showHolds', label: 'שריוני תאריך', cls: 'hold', checked: checkboxes.showHolds !== false },
                { key: 'showMeetings', label: 'פגישות', cls: 'meeting pcMeeting', checked: !!checkboxes.showMeetings },
                { key: 'showTasks', label: 'משימות', cls: 'task pcTask', checked: !!checkboxes.showTasks },
                { key: 'showHolidays', label: 'חגים', cls: 'holiday', checked: !!checkboxes.showHolidays },
                { key: 'showGoogle', label: 'יומן גוגל', cls: 'googleEvent', checked: !!checkboxes.showGoogle },
                // אישי - undefined for anyone whose saved checkboxes predate it: shown by default
                { key: 'showPersonal', label: 'אישי', cls: 'personal pcPersonal', checked: checkboxes.showPersonal !== false },
            ];
            if (this.bookingShowOnCalendar)
                chips.push({ key: 'showBookingAvailability', label: 'פנוי לתיאום פגישות', cls: 'bookingAvailability', checked: checkboxes.showBookingAvailability !== false });
            return chips;
        },

        // Online booking is a per-account service the system admin turns on (biz-services id 10)
        bookingFeatureEnabled() {
            const user = this.$store.getters.getUser || {};
            return (user.bizServices || []).map(String).indexOf('10') > -1;
        },

        // The date being edited in the day popup, as the availability endpoint returned it
        bookingDayInfo() {
            if (!this.clickedDate) return null;
            const key = this.bookingDateKey(this.clickedDate);
            return this.bookingAvailabilityDays.find(day => day.date === key) || null;
        },

        // The bookable windows as calendar items — same shape as holidays, so nothing else in the
        // calendar needs to know about them.
        bookingAvailabilityItems() {
            if (!this.bookingShowOnCalendar) return [];
            return this.bookingAvailabilityDays
                .filter(day => day.ranges && day.ranges.length && day.inHorizon)
                .map(day => {
                    const hours = day.ranges.map(range => range.from + '-' + range.to).join(', ');
                    const title = 'פנוי לתיאום פגישות ' + hours
                        + (day.freeSlots.length ? ' · ' + day.freeSlots.length + ' מועדים' : ' · מלא');
                    return {
                        id: 'bookingAvailability_' + day.date,
                        title: title,
                        originalTitle: title,
                        startDate: new Date(day.date + 'T00:00:00.000Z'),
                        classes: ['calendarItemBookingAvailability'],
                        itemType: 'bookingAvailability'
                    };
                });
        },

        dayTimeslots() {

            let startTime = parseInt(this.$store?.state?.businessSettings?.workDayStart?.match(/(.*):/)[1]);
            let endTime = parseInt(this.$store?.state?.businessSettings?.workDayEnd?.match(/(.*):/)[1]);

            if (!startTime && startTime !== 0)
                startTime = 9;

            if (!endTime)
                endTime = 21;

            let timeslots = [];

            // the week's and the day's timed items stretch the hours shown
            const shown = this.singleDayViewItems.concat.apply(this.singleDayViewItems, this.weekViewItems);

            shown.forEach(item => {

                if (!item.time)
                    return;

                let hour = parseInt(item.time.match(/(.*):/)[1]);

                if (hour < startTime)
                    startTime = hour;

                if (hour > endTime)
                    endTime = hour;

            });

            for (let i = startTime; i < endTime; i++) {
                timeslots.push(i);
            }

            return timeslots;
        },

        filteredItems() {

            let filteredItems = deepCopy(this.items);

            if (!this.$store.state.calendarCheckboxes.showEvents)
                filteredItems = filteredItems.filter(i => !(i.itemType == 'event' && i.planupSystemStatus));

            if (!this.$store.state.calendarCheckboxes.showMaybeEvents)
                filteredItems = filteredItems.filter(i => !(i.itemType == 'event' && !i.planupSystemStatus));

            // שריוני תאריך - undefined for anyone whose saved checkboxes predate them: shown by default
            if (this.$store.state.calendarCheckboxes.showHolds === false)
                filteredItems = filteredItems.filter(i => i.itemType != 'hold');

            if (!this.$store.state.calendarCheckboxes.showMeetings)
                filteredItems = filteredItems.filter(i => i.itemType != 'meeting');

            if (!this.$store.state.calendarCheckboxes.showTasks)
                filteredItems = filteredItems.filter(i => i.itemType != 'task');

            if (!this.$store.state.calendarCheckboxes.showGoogle)
                filteredItems = filteredItems.filter(i => i.itemType != 'googleEvent');

            // אישי - undefined for anyone whose saved checkboxes predate it: shown by default
            if (this.$store.state.calendarCheckboxes.showPersonal === false)
                filteredItems = filteredItems.filter(i => i.itemType != 'personal');

            if (this.$store.state.calendarCheckboxes.showHolidays) {
                filteredItems = filteredItems.concat(this.$store.state.holidays || []);
            }

            // undefined for anyone whose saved checkboxes predate this layer — shown by default
            if (this.$store.state.calendarCheckboxes.showBookingAvailability !== false) {
                filteredItems = filteredItems.concat(this.bookingAvailabilityItems);
            }

            return filteredItems.sort((a, b) => timeOf(a) - timeOf(b));
        }

    },
    data() {

        let planupCalendarDisplayTypes = [
            { name: 'חודש', value: 'month' },
            { name: 'שבוע', value: 'week' },
            { name: 'יום', value: 'day' }
        ];

        return {

            chosenTime: null,

            showCrewMembers: false,

            singleDayViewItems: [],
            singleDayViewDate: null,

            weekViewItems: [[], [], [], [], [], [], []],
            weekViewDate: null,
            weekViewDaysOfWeek: [],

            planupCalendarDisplayTypes,

            displayType: planupCalendarDisplayTypes[0],

            chosenEntityToCreateInPopup: null,

            clickedDate: null,

            showDailyAgenda: false,

            dateToShow: this.$store.state.calendarDateToShow,

            clickDateModalTitle: '',
            itemsToShowInPopup: [],

            // Online-booking availability: the bookable windows of the shown month (drawn on the
            // calendar) and the single-date editor inside the day popup.
            bookingAvailabilityDays: [],
            bookingShowOnCalendar: false,
            showBookingAvailabilityEditor: false,
            loadingBookingAvailability: false,
            savingBookingAvailability: false,
            bookingEditorOpen: true,
            bookingEditorFrom: '09:00',
            bookingEditorTo: '17:00',

            items: [],

            currentCacheKey: this.$handlers.calendarHandler.buildCacheKey(new Date())
        }
    },
    methods: {

        // ------------------------------------------------------------------ the toolbar

        // a period back (-1) or forward (1) in the open view
        shiftPeriod(direction) {

            const date = new Date(this.anchorDate);

            switch (this.displayType.value) {
                case 'week':
                    date.setDate(date.getDate() + 7 * direction);
                    this.weekViewDate = date;
                    return;
                case 'day':
                    date.setDate(date.getDate() + direction);
                    this.singleDayViewDate = date;
                    return;
            }

            if (this.period == 'week') {
                date.setDate(date.getDate() + 7 * direction);
            } else {
                date.setDate(1);
                date.setMonth(date.getMonth() + direction);
            }
            this.dateToShow = date;
        },

        // היום, or a month picked in the toolbar
        goToDate(value) {

            const date = validDate(value) || new Date();

            switch (this.displayType.value) {
                case 'week':
                    this.weekViewDate = sundayOf(date);
                    return;
                case 'day':
                    this.singleDayViewDate = date;
                    return;
            }
            this.dateToShow = date;
        },

        onSwipe(direction) {
            this.shiftPeriod(direction == 'next' ? 1 : -1);
        },

        toggleChip(key) {
            const checkboxes = this.$store.state.calendarCheckboxes;
            const value = (key == 'showBookingAvailability' || key == 'showHolds' || key == 'showPersonal') ? checkboxes[key] === false : !checkboxes[key];
            this.$set(checkboxes, key, value);
        },

        // App.vue calls this after opening the יומן modal (the swipeable triple calendar it centered
        // is gone - the month grid handles the swipe itself)
        centerMultipleCalendars() {
        },

        // ------------------------------------------------------------------- the items

        iconOf(item) {
            return itemIcon(item.itemType);
        },

        typeLabel(item) {
            return ITEM_TYPE_LABELS[item.itemType] || this.$t(item.itemType);
        },

        timeslotClasses(item) {
            return ['timeSlotElement', item.itemType].concat(item.colorClasses || []);
        },

        hourOf(time) {
            return time.substring(0, time.indexOf(':'));
        },

        isToday(date) {
            if (!date)
                return false;
            const today = new Date();
            return date.getDate() == today.getDate() && date.getMonth() == today.getMonth() && date.getFullYear() == today.getFullYear();
        },

        hoverEnter(item, element) {
            if (this.$refs.hoverCard)
                this.$refs.hoverCard.schedule(item, element);
        },

        hoverLeave() {
            if (this.$refs.hoverCard)
                this.$refs.hoverCard.hide();
        },

        updateWeekViewDate(newVal) {

            const days = [];
            const items = [];

            for (let i = 0; i < 7; i++) {
                let nextDayInWeek = new Date(newVal);
                nextDayInWeek.setDate(newVal.getDate() + i);
                days.push(nextDayInWeek);
                items.push(this.findItemsInThisDay(nextDayInWeek));
            }

            this.weekViewDaysOfWeek = days;
            this.weekViewItems = items;
        },

        // the duration bar under an item of the week / day views
        calculateTimeslotStyle(item) {

            if (!item.endTime || !item.time)
                return `height:12px;`;

            let startHour = +item.time.split(':')[0];
            let endHour = +item.endTime.split(':')[0];

            let height = ((endHour - startHour) * 40) - 28;

            return `height:${Math.max(12, height)}px;`;
        },

        // a click on an hour of the week (date given) or the day view
        clickOnTimeSlot(time, date) {

            const day = date || this.singleDayViewDate || new Date();
            const hasTime = time !== undefined && time !== null;

            if (this.disableClickDatePopup) {
                this.$emit('chooseTime', time);
                return;
            }

            this.clickedDate = day;
            this.itemsToShowInPopup = this.findItemsInThisDay(day);
            this.showDailyAgenda = this.showTodayAgendaOnPopup;
            this.chosenEntityToCreateInPopup = null;
            this.showBookingAvailabilityEditor = false;
            this.chosenTime = hasTime ? time : null;
            this.clickDateModalTitle = ' מה תרצו לעשות בתאריך ' + this.$globals.niceDate(day) + (hasTime ? ' בשעה ' + time + ':00' : '') + '?';
            this.hoverLeave();
            this.$refs.clickDateModal.show();
        },

        setDayView() {

            this.displayType = this.planupCalendarDisplayTypes.find(pcdt => pcdt.value == 'day');

            if (this.clickedDate)
                this.singleDayViewDate = this.clickedDate;

            this.singleDayViewItems = this.findItemsInThisDay(this.singleDayViewDate);

            this.$refs.clickDateModal.hide()    //leave this line here
        },

        setMonthView() {

            this.displayType = this.planupCalendarDisplayTypes.find(pcdt => pcdt.value == 'month');
        },

        setWeekView() {

            this.displayType = this.planupCalendarDisplayTypes.find(pcdt => pcdt.value == 'week');
        },

        emitNewEntityChoice(customerChoice) {

            this.$refs.clickDateModal.hide();

            let time = null;
            if (this.chosenTime || this.chosenTime === 0)
                time = this.chosenTime + ':00';

            switch (`${this.chosenEntityToCreateInPopup} ${customerChoice}`) {

                case 'event newCustomer':
                    this.$handlers.customerHandler.goToAddCustomerAddEvent(this.clickedDate, time);
                    break;

                case 'event existingCustomer':
                    this.$root.$emit('findCustomer', this.$handlers.eventHandler.gotoAddNewEventToCustomerFromSearchResult, this.clickedDate, time);
                    break;

                case 'meeting newCustomer':
                    this.$handlers.customerHandler.goToAddCustomerOnlyMeeting(this.clickedDate, time);
                    break;

                case 'meeting existingCustomer':
                    this.$root.$emit('findCustomer', this.$handlers.meetingHandler.gotoAddNewMeetingFromSearch, this.clickedDate, time);
                    break;

                case 'meeting withoutCustomer':
                    this.$globals.findComponent('App').openMeetingWithoutCustomerModal(this.clickedDate, null, time);
                    break;
            }

        },

        // the items the calendar shows on that day (the chips apply here too), all-day first
        findItemsInThisDay(date) {
            return itemsOfDay(this.filteredItems, date);
        },

        clickDate(date) {

            this.clickedDate = date;
            this.chosenTime = null;
            this.hoverLeave();

            if (this.disableClickDatePopup) {
                this.$emit('chooseDate', date);
                return;
            }

            this.itemsToShowInPopup = this.findItemsInThisDay(date);
            this.showDailyAgenda = this.showTodayAgendaOnPopup;
            this.chosenEntityToCreateInPopup = null;
            this.showBookingAvailabilityEditor = false;
            this.$refs.clickDateModal.show();

            //this line of clickDateModalTitle was before the option to also have a "do you want to continue with this date" in the same clickDateModal.
            //we use a prop to distinguish the two options
            this.clickDateModalTitle = ' מה תרצו לעשות בתאריך ' + this.$globals.niceDate(date) + '?';

        },

        clickItem(item) {

            this.hoverLeave();

            if (this.disableItemClicks)
                return this.clickDate(localDayOf(item));

            let itemType = item.id.split('_')[0];
            let itemId = item.id.split('_')[1];

            switch (itemType) {
                case 'event':
                    this.$globals.closeAllModals();
                    this.$router.push(`/eventToCustomer/${itemId}`)
                    return;
                case 'meeting':
                    this.$globals.closeAllModals();
                    this.$router.push(`/meetingToCustomer/${itemId}`)
                    return;
                case 'task':
                    this.$globals.closeAllModals();
                    return this.$handlers.tasksHandler.getTask(itemId).then(task => this.$root.$emit('openTaskModal', task))
                case 'holiday':
                    return;
                case 'hold':
                    // a held option of an undated event - its customer card
                    this.$globals.closeAllModals();
                    this.$router.push(`/eventToCustomer/${itemId}`)
                    return;
                case 'bookingAvailability':
                    // The green strip is the booking window itself — clicking it edits that date
                    this.clickDate(localDayOf(item));
                    this.openBookingAvailabilityEditor();
                    return;
                case 'personal':
                    // אישי - its form (PersonalItemModal, App.vue), fetched by id
                    this.$globals.closeAllModals();
                    this.$root.$emit('openPersonalItemModal', { id: itemId });
                    return;
            }

            this.$swal(item.title);
        },

        updateItems(value) {

            const date = validDate(value) || new Date();

            let firstOfMonth = new Date(date.toISOString());
            firstOfMonth.setDate(1);

            this.$handlers.calendarHandler.getItemsInMonth(firstOfMonth).then(items => {

                this.items = items;
                this.currentCacheKey = this.$handlers.calendarHandler.buildCacheKey(firstOfMonth);
            });

            this.loadBookingAvailability(date);
        },

        // ---------------------------------------------------------------- online booking availability

        bookingDateKey(date) {
            const d = new Date(date);
            const month = d.getMonth() + 1, day = d.getDate();
            return d.getFullYear() + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day;
        },

        // The bookable windows of the shown month (padded, because the month grid also shows a few
        // days of the neighbouring months).
        loadBookingAvailability(date) {

            if (!this.bookingFeatureEnabled || !date)
                return Promise.resolve();

            const shown = new Date(date);
            const from = this.bookingDateKey(new Date(shown.getFullYear(), shown.getMonth(), 1 - 7));
            const to = this.bookingDateKey(new Date(shown.getFullYear(), shown.getMonth() + 1, 7));

            this.loadingBookingAvailability = true;

            return this.$handlers.calendarHandler.getBookingAvailability(from, to).then(data => {
                this.bookingAvailabilityDays = (data && data.days) || [];
                this.bookingShowOnCalendar = !!(data && data.enabled && data.showOnCalendar);
                this.loadingBookingAvailability = false;
            });
        },

        openBookingAvailabilityEditor() {

            this.showBookingAvailabilityEditor = true;

            const seedFromInfo = () => {
                const ranges = (this.bookingDayInfo && this.bookingDayInfo.ranges) || [];
                this.bookingEditorOpen = ranges.length > 0;
                this.bookingEditorFrom = ranges.length ? ranges[0].from : '09:00';
                this.bookingEditorTo = ranges.length ? ranges[0].to : '17:00';
            };

            if (this.bookingAvailabilityDays.length) {
                seedFromInfo();
                return;
            }

            this.loadBookingAvailability(this.clickedDate || this.dateToShow).then(seedFromInfo);
        },

        saveBookingAvailability() {

            if (this.bookingEditorOpen && this.bookingEditorFrom >= this.bookingEditorTo) {
                this.$swal('שעת הסיום חייבת להיות אחרי שעת ההתחלה');
                return;
            }

            this.postBookingAvailability(this.bookingEditorOpen ? [{ from: this.bookingEditorFrom, to: this.bookingEditorTo }] : []);
        },

        clearBookingAvailability() {
            this.postBookingAvailability(null);
        },

        postBookingAvailability(ranges) {

            const date = this.bookingDateKey(this.clickedDate);

            this.savingBookingAvailability = true;

            apiPost('booking/day-availability', { date: date, ranges: ranges }).then(() => {
                this.savingBookingAvailability = false;
                this.showBookingAvailabilityEditor = false;
                this.$refs.clickDateModal.hide();
                this.$handlers.calendarHandler.resetBookingAvailability();
                this.loadBookingAvailability(this.dateToShow);
            }).catch(() => {
                this.savingBookingAvailability = false;
                this.$swal('שגיאה בשמירת הזמינות');
            });
        },
    },
    mounted() {

        this.$handlers.calendarHandler.registerCalendarComponent(this);
        this.updateItems(this.anchorDate);

    },

    components: { CalendarToolbar, CalendarMonthGrid, CalendarHoverCard, WheelTimePicker }
}
</script>
<style>
/* ---------------------------------------------------------------- the colors of every item
   The --pc variables (calendar-colors.js) per item type and status; the account's chosen colors
   override them from the dynamic stylesheet. Order matters: a status rule comes after the type's. */
.pcal {
    --pc-rgb: 100, 116, 139;
    --pc-ink: #475569;
    --pc-alpha: .13;
    --pc-border-alpha: 0;
    --pc-stripe: 0;
    --pc-hover: 0;
}

.pcal .event,
.pcal .pcSys_hazmanaSigned {
    --pc-rgb: 227, 24, 93;
    --pc-ink: #e3185d;
}

.pcal .pcSys_pniaInTipul,
.pcal .pcSys_priceOfferSent {
    --pc-rgb: 171, 140, 228;
    --pc-ink: #816aac;
    --pc-alpha: 0;
    --pc-border-alpha: .55;
}

.pcal .pcSys_hazmanaSent {
    --pc-rgb: 171, 140, 228;
    --pc-ink: #816aac;
    --pc-alpha: .06;
    --pc-stripe: 1;
}

.pcal .pcSys_newLead {
    --pc-rgb: 217, 70, 239;
    --pc-ink: #a333b4;
}

.pcal .meeting,
.pcal .pcMeeting {
    --pc-rgb: 102, 218, 189;
    --pc-ink: #3f8f7b;
}

.pcal .task,
.pcal .pcTask {
    --pc-rgb: 23, 179, 199;
    --pc-ink: #128a99;
}

/* a meeting whose סטטוס says it does not take place (בוטלה / הבריזו - item-mapper's pcalMeetingOff):
   struck through and faded, in every view - its time is free */
.pcal .pcalMeetingOff {
    opacity: .55;
}

.pcal .pcalMeetingOff .pcal-item-title {
    text-decoration: line-through;
}

.pcal .holiday {
    --pc-rgb: 245, 180, 0;
    --pc-ink: #8a6500;
}

/* אישי (מילואים / רופא / חופשה): a warm gray - the one thing on the calendar that is not business
   (calendar-colors.js DEFAULT_PERSONAL_COLOR; הגדרות → צבעי לוח שנה → אישי overrides it) */
.pcal .personal,
.pcal .pcPersonal {
    --pc-rgb: 120, 113, 108;
    --pc-ink: #57534e;
}

.pcal .googleEvent {
    --pc-rgb: 148, 163, 211;
    --pc-ink: #5f6b94;
}

.pcal .bookingAvailability {
    --pc-rgb: 47, 181, 122;
    --pc-ink: #237f58;
}

/* ---------------------------------------------------------------- the week / day views */
.pcal-timegrid {
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
}

.pcal-timegrid-scroll {
    height: calc(100vh - 230px);
    min-height: 420px;
    overflow-y: auto;
}

.pcal-timegrid-sticky {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
}

.timeslotTable {
    width: 100%;
    border-collapse: collapse;
}

.timeslotTable tr {
    border-bottom: 1px solid #eef2f7;
}

.timeslotTable td {
    padding: 4px 5px;
    vertical-align: top;
}

.timeslotTable .pcal-tg-hour {
    width: 64px;
    padding-top: 7px;
    text-align: center;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: #64748b;
    white-space: nowrap;
}

.timeslotTable .pcal-tg-cell {
    min-height: 42px;
    height: 42px;
    border-right: 1px solid #eef2f7;
}

.timeslotTable .pcal-tg-cell.clickableTimeSlot {
    cursor: pointer;
}

.timeslotTable .pcal-tg-cell.clickableTimeSlot:hover {
    background: #f8fafc;
}

.timeslotTable .pcal-tg-cell.today,
.timeslotTable .pcal-tg-dayhead.today {
    background: #f3f8ff;
}

.timeslotTable .pcal-tg-dayhead {
    padding: 10px 6px 8px;
    text-align: center;
    border-right: 1px solid #eef2f7;
    background: #f8fafc;
}

.pcal-tg-dayname {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.pcal-tg-daynum {
    font-size: 24px;
    font-weight: 700;
    line-height: 28px;
    color: #1f2937;
    margin-bottom: 4px;
}

.timeslotTable .pcal-tg-dayhead.today .pcal-tg-daynum {
    color: #2563eb;
}

.planupDayView .timeslotTable tr.clickableTimeSlot {
    cursor: pointer;
}

.planupDayView .timeslotTable tr.clickableTimeSlot:hover {
    background: #f8fafc;
}

.planupDayView .timeslotTable .pcal-tg-cell {
    text-align: right;
    height: 44px;
}

.planupDayView .timeslotTable .pcal-tg-allday .pcal-tg-cell {
    background: #fafbfc;
}

/* an item of the week / day views, drawn like a month pill */
.timeSlotElement {
    position: relative;
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 26px;
    margin: 2px 0;
    padding: 2px 6px 2px 8px;
    border-radius: 6px;
    border: 1px solid rgba(var(--pc-rgb), var(--pc-border-alpha));
    background-color: rgba(var(--pc-rgb), calc(var(--pc-alpha) + var(--pc-hover)));
    background-image: repeating-linear-gradient(135deg, rgba(var(--pc-rgb), calc(.18 * var(--pc-stripe))) 0 4px, rgba(var(--pc-rgb), calc(.04 * var(--pc-stripe))) 4px 9px);
    color: var(--pc-ink);
    font-size: 12.5px;
    line-height: 1.25;
    text-align: right;
    cursor: pointer;
    overflow: hidden;
    transition: transform .12s, box-shadow .12s, background-color .12s;
}

.timeSlotElement:hover {
    --pc-hover: .1;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(15, 23, 42, .12);
}

.timeSlotElement .pcal-item-icon {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(var(--pc-rgb), .2);
    color: var(--pc-ink);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
}

.timeSlotElement .pcal-item-time {
    flex: 0 0 auto;
    font-size: 11.5px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    direction: ltr;
}

.timeSlotElement .pcal-item-title {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 600;
}

.timeSlotElement .pcal-item-sub {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 11px;
    opacity: .82;
}

/* the duration bar: the item's tint continues below it for the hours it takes */
.timeSlotElementEndTime {
    position: absolute;
    left: 0;
    width: 100%;
    height: 12px;
    border-radius: 0 0 6px 6px;
    background: rgba(var(--pc-rgb), calc(var(--pc-alpha) + .03));
    pointer-events: none;
}

.planupWeekView .timeSlotElement {
    font-size: 11.5px;
    gap: 4px;
    padding: 2px 4px 2px 6px;
    margin: 1px 0;
}

.planupWeekView .timeSlotElement .pcal-item-icon {
    flex-basis: 16px;
    width: 16px;
    height: 16px;
    font-size: 8px;
}

/* the sticky day-header table and the time-grid table are two tables sharing column edges; fixed
   layout keeps the declared widths whatever the titles (tests/test-calendar-week-alignment.js) */
.planupWeekView .timeslotTable {
    table-layout: fixed;
    min-width: 720px;
    background: white;
}

.planupWeekView .timeslotTable .pcal-tg-hour {
    width: calc(12.5% - 16px);
}

.planupWeekView .timeslotTable .pcal-tg-dayhead,
.planupWeekView .timeslotTable .pcal-tg-cell {
    width: 12.5%;
}

.planupCalendarDisplay_day {
    min-width: unset !important;
}

/* ---------------------------------------------------------------- the day popup's agenda */
.busyDayPopupTable tr:nth-of-type(odd) {
    background: rgba(51, 68, 109, .03);
}

.busyDayPopupTable tr {
    cursor: pointer;
}

.busyDayPopupTable tr:hover {
    background: #f1f5f9;
}

.busyDayPopupTable td {
    padding: 6px 5px;
    vertical-align: middle;
}

.pcal-agenda-time {
    width: 60px;
    text-align: center;
    color: #64748b;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
}

/* (three classes: index.html still carries .clickDateModalRow_* rules with the old solid colors) */
.pcal .busyDayPopupTable .pcal-agenda-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 3px 8px;
    border-radius: 6px;
    background: rgba(var(--pc-rgb), .15);
    color: var(--pc-ink);
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;
}

.pcal-agenda-chip i {
    font-size: 11px;
}

.pcal-agenda-muted {
    color: #64748b;
    font-size: 13px;
}

.pcal-agenda-title {
    font-weight: 600;
    color: #1e293b;
}

.pcal-agenda-sub {
    display: block;
    font-size: 12px;
    color: #64748b;
}

@media (max-width: 767px) {

    .pcal-timegrid-scroll {
        height: calc(100vh - 190px);
        min-height: 320px;
    }

    .timeslotTable .pcal-tg-hour {
        width: 46px;
        font-size: 11px;
    }

    .pcal-tg-daynum {
        font-size: 18px;
        line-height: 22px;
    }

    .pcal-tg-dayname {
        font-size: 11px;
    }

    .timeSlotElement .pcal-item-sub {
        display: none;
    }
}
</style>
